import type {MechanicalContextDocumentDataSubset, IExpressionEvaluationProjectContext} from "@buildwithflux/core";
import type {
    EnterpriseHandle,
    EnterpriseUid,
    IDocumentData,
    IElementsMap,
    INetsMap,
    OrganizationUid,
    PartSourcingProperties,
} from "@buildwithflux/models";
import {useShallow} from "zustand/react/shallow";

import {useFluxServices} from "../../../../injection/hooks";
import {select} from "../selectors";

/**
 * Hook to get the full document data
 * Uses shallow comparison to prevent unnecessary re-renders
 */
export const useDocument = (): IDocumentData | null => {
    return useFluxServices().useDocumentStore(useShallow(select.document.data));
};

/**
 * Hook to get the document UID
 */
export const useDocumentUid = (): string | undefined => {
    return useFluxServices().useDocumentStore(select.document.uid);
};

/**
 * Hook to get the document name
 */
export const useDocumentName = (): string | undefined => {
    return useFluxServices().useDocumentStore(select.document.name);
};

/**
 * Hook to get the document description
 */
export const useDocumentDescription = (): string | undefined => {
    return useFluxServices().useDocumentStore(select.document.description);
};

/**
 * Hook to get the document slug
 */
export const useDocumentSlug = (): string | undefined => {
    return useFluxServices().useDocumentStore(select.document.slug);
};

/**
 * Hook to get the document owner handle
 */
export const useDocumentOwnerHandle = (): string | undefined => {
    return useFluxServices().useDocumentStore(select.document.ownerHandle);
};

/**
 * Hook to get the document owner UID
 */
export const useDocumentOwnerUid = (): string | undefined => {
    return useFluxServices().useDocumentStore(select.document.ownerUid);
};

/**
 * Hook to check if the document is loaded
 */
export const useIsDocumentLoaded = (): boolean => {
    return useFluxServices().useDocumentStore(select.document.isLoaded);
};

/**
 * Hook to check if the document is loading
 */
export const useIsDocumentLoading = (): boolean => {
    return useFluxServices().useDocumentStore(select.document.isLoading);
};

/**
 * Hook to check if the document is published (belongs to a part)
 */
export const useIsDocumentPublished = (): boolean => {
    return useFluxServices().useDocumentStore(select.document.isPublished);
};

/**
 * Hook to check if the document is a local component
 */
export const useIsLocalComponent = (): boolean => {
    return useFluxServices().useDocumentStore(select.document.isLocalComponent);
};

/**
 * Hook to get the document owner's profile URL - memoized
 */
export const useDocumentOwnerProfileUrl = (): string | undefined => {
    return useFluxServices().useDocumentStore(select.document.ownerProfileUrl);
};

/**
 * Hook to get the document preview image
 */
export const useDocumentPreviewImage = (): string | undefined => {
    return useFluxServices().useDocumentStore(select.document.previewImage);
};

/**
 * Hook to get the document fork count
 */
export const useDocumentForkCount = (): number | undefined => {
    return useFluxServices().useDocumentStore(select.document.forkCount);
};

/**
 * Hook to get the document star count
 */
export const useDocumentStarCount = (): number | undefined => {
    return useFluxServices().useDocumentStore(select.document.starCount);
};

/**
 * Hook to get the document assets
 */
export const useDocumentAssets = (): IDocumentData["assets"] => {
    return useFluxServices().useDocumentStore(useShallow(select.document.assets));
};

/**
 * Hook to get the document controls
 */
export const useDocumentControls = (): IDocumentData["controls"] | undefined => {
    return useFluxServices().useDocumentStore(useShallow(select.document.controls));
};

/**
 * Hook to get the document behaviour (code)
 */
export const useDocumentBehaviour = (): string | undefined => {
    return useFluxServices().useDocumentStore(select.document.behaviour);
};

/**
 * Hook to check if the document has any elements
 */
export const useDocumentHasElements = (): boolean => {
    return useFluxServices().useDocumentStore(select.document.hasElements);
};

/**
 * Hook to check if the document has any nets
 */
export const useDocumentHasNets = (): boolean => {
    return useFluxServices().useDocumentStore(select.document.hasNets);
};

/**
 * Hook to check if the document has any routes
 */
export const useDocumentHasRoutes = (): boolean => {
    return useFluxServices().useDocumentStore(select.document.hasRoutes);
};

/**
 * Hook to check if the document has any comment threads
 */
export const useDocumentHasCommentThreads = (): boolean => {
    return useFluxServices().useDocumentStore(select.document.hasCommentThreads);
};

/**
 * Hook to get the document updated_at timestamp
 */
export const useDocumentUpdatedAt = (): number | undefined => {
    return useFluxServices().useDocumentStore(select.document.updatedAt);
};

/**
 * Hook to get the document created_at timestamp
 */
export const useDocumentCreatedAt = (): number | undefined => {
    return useFluxServices().useDocumentStore(select.document.createdAt);
};

/**
 * Hook to get the document updated_by user UID
 */
export const useDocumentUpdatedBy = (): string | undefined => {
    return useFluxServices().useDocumentStore(select.document.updatedBy);
};

/**
 * Hook to get the fork_of_document_uid
 */
export const useDocumentForkOfDocumentUid = (): string | undefined => {
    return useFluxServices().useDocumentStore(select.document.forkOfDocumentUid);
};

/**
 * Hook to get the belongs_to_part_uid
 */
export const useBelongsToPartUid = (): string | undefined => {
    return useFluxServices().useDocumentStore(select.document.belongsToPartUid);
};

/**
 * Hook to get the localToDocumentUid (for local components)
 */
export const useLocalToDocumentUid = (): string | undefined => {
    return useFluxServices().useDocumentStore(select.document.localToDocumentUid);
};

/**
 * Hook to get the organization owner UID
 */
export const useDocumentOrganizationOwnerUid = (): OrganizationUid | undefined => {
    return useFluxServices().useDocumentStore(select.document.organizationOwnerUid);
};

/**
 * Hook to get the enterprise owner UID
 */
export const useDocumentEnterpriseOwnerUid = (): EnterpriseUid | undefined => {
    return useFluxServices().useDocumentStore(select.document.enterpriseOwnerUid);
};

/**
 * Hook to get the enterprise owner handle
 */
export const useDocumentEnterpriseOwnerHandle = (): EnterpriseHandle | undefined => {
    return useFluxServices().useDocumentStore(select.document.enterpriseOwnerHandle);
};

/**
 * Hook to check if the organization owner is the enterprise default
 */
export const useDocumentOrganizationOwnerIsEnterpriseDefault = (): boolean | undefined => {
    return useFluxServices().useDocumentStore(select.document.organizationOwnerIsEnterpriseDefault);
};

/**
 * Hook to get the document contributor UIDs
 * Uses shallow comparison to prevent unnecessary re-renders
 */
export const useDocumentContributorUids = (): string[] | undefined => {
    return useFluxServices().useDocumentStore(useShallow(select.document.contributorUids));
};

/**
 * Hook to get the document roles
 * Uses shallow comparison to prevent unnecessary re-renders
 */
export const useDocumentRoles = (): IDocumentData["roles"] | undefined => {
    return useFluxServices().useDocumentStore(useShallow(select.document.roles));
};

/**
 * Hook to get the 3D model asset storage name from the document assets
 */
export const useDocument3dModelAssetStorageName = (): string | undefined => {
    return useFluxServices().useDocumentStore(select.document.document3DModelAssetStorageName);
};

/**
 * Hook to get the document package code from properties
 */
export const useDocumentPackageCode = (): string | undefined => {
    return useFluxServices().useDocumentStore(select.document.documentPackageCode);
};

/**
 * Hook to get the document configs
 * Uses shallow comparison to prevent unnecessary re-renders
 */
export const useDocumentConfigs = (): IDocumentData["configs"] | undefined => {
    return useFluxServices().useDocumentStore(useShallow(select.document.configs));
};

/**
 * Hook to get the organization member permission type
 */
export const useDocumentOrganizationMemberPermissionType = ():
    | IDocumentData["organization_member_permission_type"]
    | undefined => {
    return useFluxServices().useDocumentStore(select.document.organizationMemberPermissionType);
};

/**
 * Hook to get the enterprise member permission type
 */
export const useDocumentEnterpriseMemberPermissionType = ():
    | IDocumentData["enterprise_member_permission_type"]
    | undefined => {
    return useFluxServices().useDocumentStore(select.document.enterpriseMemberPermissionType);
};

/**
 * Hook to get the receiveLatestDraftsForPartUids
 * Uses shallow comparison to prevent unnecessary re-renders
 */
export const useReceivingLatestDraftsForParts = (): IDocumentData["receiveLatestDraftsForPartUids"] | undefined => {
    return useFluxServices().useDocumentStore(useShallow(select.document.receiveLatestDraftsForPartUids));
};

/**
 * Hook to get the document invites
 * Uses shallow comparison to prevent unnecessary re-renders
 */
export const useDocumentInvites = (): IDocumentData["invites"] | undefined => {
    return useFluxServices().useDocumentStore(useShallow(select.document.invites));
};

/**
 * Hook to get the document slug with fallback to documentMeta.baseDocument
 * This is useful when the full document hasn't loaded yet but baseDocument is available
 */
export const useDocumentSlugWithFallback = (): string | undefined => {
    const {useDocumentStore, useDocumentMetaStore} = useFluxServices();
    const documentSlug = useDocumentStore((state) => state.slug);
    const baseDocumentSlug = useDocumentMetaStore((state) => state.baseDocument?.slug);
    return documentSlug ?? baseDocumentSlug;
};

/**
 * Hook to get the document title (name) with fallback to documentMeta.baseDocument
 * This is useful when the full document hasn't loaded yet but baseDocument is available
 */
export const useDocumentTitleWithFallback = (): string | undefined => {
    const {useDocumentStore, useDocumentMetaStore} = useFluxServices();
    const documentName = useDocumentStore((state) => state.name);
    const baseDocumentName = useDocumentMetaStore((state) => state.baseDocument?.name);
    return documentName ?? baseDocumentName;
};

/**
 * Hook to get the document description with fallback to documentMeta.baseDocument
 * This is useful when the full document hasn't loaded yet but baseDocument is available
 */
export const useDocumentDescriptionWithFallback = (): string | undefined => {
    const {useDocumentStore, useDocumentMetaStore} = useFluxServices();
    const documentDescription = useDocumentStore((state) => state.description);
    const baseDocumentDescription = useDocumentMetaStore((state) => state.baseDocument?.description);
    return documentDescription ?? baseDocumentDescription;
};

/**
 * Hook to get the anonymous role from the document roles
 * NOTE: This is a read-only hook. The Redux selector (selectAnonRole) calls
 * DocumentStorageHelper.getOrSetAnonRole which can mutate the document data.
 * This hook just reads the existing anon role.
 * @returns The anonymous role permissions, or undefined if not set
 */
export const useDocumentAnonRole = (): IDocumentData["roles"] extends Record<string, infer T>
    ? T | undefined
    : never => {
    return useFluxServices().useDocumentStore(select.document.anonRole);
};

/**
 * Hook to check if a document has advanced permissions
 * @returns true if the document has advanced permissions
 */
export const useDocumentHasAdvancedPermissions = (): boolean => {
    return useFluxServices().useDocumentStore(select.document.hasAdvancedPermissions);
};

/**
 * Hook to get the manufacturer part number property from the document
 * Returns the property object containing the MPN value
 */
export const useDocumentManufacturerPartNumber = (): ReturnType<typeof select.document.manufacturerPartNumber> => {
    return useFluxServices().useDocumentStore(select.document.manufacturerPartNumber);
};

/**
 * Hook to get document-level part sourcing properties from the Zustand store.
 * Returns manufacturer name, manufacturer part number, part name, and part UID.
 * @returns PartSourcingProperties or null if document not found
 */
export const useDocumentPartSourcingProperties = (): PartSourcingProperties | null => {
    return useFluxServices().useDocumentStore(useShallow(select.document.partSourcingProperties));
};

/**
 * Hook to get the project expression context from the Zustand store.
 * This is used for evaluating expressions in the document.
 */
export const useProjectExpressionContext = (): IExpressionEvaluationProjectContext | undefined => {
    return useFluxServices().useDocumentStore(select.document.projectExpressionContext);
};

/**
 * Hook to get the document elements map from the Zustand store
 * Uses shallow comparison to avoid unnecessary re-renders
 */
export const useDocumentElements = (): IElementsMap | undefined => {
    return useFluxServices().useDocumentStore(useShallow(select.elements.all));
};

/**
 * Hook to get the document nets map from the Zustand store
 * Uses shallow comparison to avoid unnecessary re-renders
 */
export const useDocumentNets = (): INetsMap | undefined => {
    return useFluxServices().useDocumentStore(useShallow(select.nets.all));
};

/**
 * Hook to get the mechanical document subset from the Zustand store.
 * This subset contains only the data needed for mechanical context calculations.
 * Uses a memoized selector to avoid creating a new object on every store update.
 */
export const useMechanicalDocumentSubSet = (): MechanicalContextDocumentDataSubset | undefined => {
    return useFluxServices().useDocumentStore(useShallow(select.document.mechanicalSubset));
};

/**
 * Hook to check if the document is a learning document (Learn Flux project)
 */
export const useIsLearningDocument = (): boolean | undefined => {
    return useFluxServices().useDocumentStore(select.document.isLearningDocument);
};
